import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomSupplierRatingSplitView from './CustomSupplierRatingSplitView';

const supplierRatingTrackingLabels = getLabels('Supplier Rating');
const supplierRatingTrackingBase = createTrackingConfig('Supplier Rating');

export const supplierRatingConfig = {
  entityName: 'SupplierRating',
  
  // Entity information for file attachments and audit logs
  entityTypeId: 9, // Supplier Rating entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/supplier-rating',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      suppliers: '/supplier'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/supplier-rating'
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth // From app config: '60%'
  },

  // Features configuration
  features: {
    fileAttachments: false, // Disable file attachment functionality for supplier ratings
  },

  // Custom Split View Panel configuration for Supplier Rating module
  customSplitViewPanel: {
    enabled: true,
    component: CustomSupplierRatingSplitView  // Use the custom Supplier Rating split view component
  },
  
  // Custom handlers for Supplier Rating-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in supplierRatingPage
    onDelete: null, // Will be defined in supplierRatingPage
    onLoad: null, // For any special loading logic
    onFormOpen: null, // Will be defined in supplierRatingPage
    
    // Supplier Rating-specific actions
    onCalculateAverage: null, // Will handle average rating calculation
    onGenerateReport: null, // Will handle rating reports
    onCompareRatings: null, // Will handle rating comparison
    
    // Board functionality (if timeline/board views are needed)
    onBoardDrop: async ({ item, fromGroup, toGroup }) => {
      if (toGroup === fromGroup) return;
      // Could be used for score ranges or status grouping
      console.log('Board drop not typically used for ratings');
    }
  },

  // Service configuration (reference only for generic service mapping)
  service: {
    name: 'supplierRatingService',
    methods: {
      getAll: 'getAllSupplierRatings',
      getById: 'getSupplierRatingById',
      create: 'createSupplierRating',
      update: 'updateSupplierRating',
      delete: 'deleteSupplierRating',
      validate: 'validateData'
    }
  },

  // Display Configuration
  displayName: 'Supplier Rating Management',
  description: 'Comprehensive supplier performance evaluation and rating tracking system',
  icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',

  // Fields to remove from forms before submission (relations/computed)
  fieldsToRemove: [
    'id',
    'supplier',
    'createdBy',
    'updatedBy',
    'createdAt',
    'updatedAt',
    'createdById',
    'updatedById',
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'supplierId',
      label: 'Entity',
      type: 'select',
      required: true,
      lookupEndpoint: 'suppliers',
      options: [],
      table: {
        header: 'Entity',
        width: '220px',
        sortable: true,
        displayField: 'supplier'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid entity'
      }
    },
    {
      name: 'date',
      label: 'Rating Date',
      type: 'date',
      required: true,
      table: {
        header: 'Date',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        required: true
      }
    },
    {
      name: 'score',
      label: 'Score',
      type: 'number',
      placeholder: '0 - 100',
      min: 0,
      max: 100,
      step: '1',
      table: { header: 'Score', width: '100px', sortable: true },
      filterable: true,
      validation: {
        min: 0,
        max: 100
      }
    },
    {
      name: 'scoreCriteria',
      label: 'Criteria',
      type: 'textarea',
      placeholder: 'Describe evaluation criteria (quality, delivery, cost...)',
      rows: 3,
      table: { header: 'Criteria', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000
      }
    },
    {
      name: 'scoreComments',
      label: 'Comments',
      type: 'textarea',
      placeholder: 'Additional comments on rating',
      rows: 3,
      table: { header: 'Comments', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000
      }
    }
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    return columns;
  },

  exportFileName: 'qms_supplier_ratings',

  // Backend field mappings
  fieldMappings: {
    supplier: 'supplier.name'
  },

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    score: 'desc', // Highest scores first
    date: 'desc' // Most recent first
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'supplierId', 'score','date'
      ]
    },
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Entity & Date',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['supplierId', 'date'],
      fieldSpans: {
        'supplierId': 'md:col-span-12',
        'date': 'md:col-span-4',
      }
    },

    {
      title: 'Evaluation',
      icon: '⭐',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['score', 'scoreCriteria', 'scoreComments'],
      fieldSpans: {
        'score': 'md:col-span-4',
        'scoreCriteria': 'md:col-span-12',
        'scoreComments': 'md:col-span-12'
      }
    }
  ],

  // Tracking — show audit users by display name (nested objects from API / selected row)
  trackingSection: {
    ...supplierRatingTrackingBase,
    fields: supplierRatingTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: supplierRatingTrackingLabels.createdDateLabel,
            userEmptyText: supplierRatingTrackingLabels.notSpecifiedText,
            dateEmptyText: supplierRatingTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: supplierRatingTrackingLabels.updatedDateLabel,
              userEmptyText: supplierRatingTrackingLabels.notModifiedText,
              dateEmptyText: supplierRatingTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined Supplier Rating labels + custom overrides)
  labels: {
    // Using predefined Supplier Rating labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Supplier Rating',
    plural: 'Supplier Ratings',
    pageTitle: 'Supplier Rating Management',
    pageSubtitle: 'Comprehensive supplier performance evaluation and tracking',
    searchPlaceholder: 'Search supplier ratings by supplier or score...',
    viewTitle: 'View Supplier Rating',
    noSelectionText: 'Select a supplier rating to see details',
    
    // Action labels
    calculateAverage: 'Calculate Average',
    generateReport: 'Generate Report',
    compareRatings: 'Compare Ratings',
    viewTrends: 'View Trends'
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    delete transformed.id;
    delete transformed.supplier;
    delete transformed.createdBy;
    delete transformed.updatedBy;
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = ['supplierId'];
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert numeric fields
    const numericFields = ['score'];
    numericFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseFloat(transformed[field]);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert date fields to ISO 8601 strings
    const dateFields = ['date'];
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue.toISOString();
          } else {
            delete transformed[field];
          }
        }
      }
    });

    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading entity ratings...',
    noData: 'No entity ratings found. Create your first entity rating to get started.',
    loadError: 'Failed to load entity ratings',
    saveSuccess: 'Entity rating saved successfully!',
    saveError: 'Failed to save entity rating',
    deleteSuccess: 'Entity rating deleted successfully!',
    deleteError: 'Failed to delete entity rating',
    deleteConfirm: 'Are you sure you want to delete this entity rating? This action cannot be undone.',
    calculateAverageSuccess: 'Average rating calculated successfully!',
    reportGeneratedSuccess: 'Rating report generated successfully!',
    comparisonGeneratedSuccess: 'Rating comparison generated successfully!'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    supplierId: { short: 'Select the entity being evaluated and rated.' },
    date: { short: 'Date when the entity evaluation and rating was conducted.' },
    score: { short: 'Overall performance score from 0 to 100 based on evaluation criteria.' },
    scoreCriteria: { short: 'Description of the evaluation criteria used (quality, delivery, cost, service, etc.).' },
    scoreComments: { short: 'Additional comments, observations, or notes about the entity performance.' }
  }
};

export default supplierRatingConfig;

